import { MessageSquare, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDesignColors } from '../../hooks/useDesignColors';
import { ChatInterface } from '../chat/ChatInterface';
import { MiniAppModal } from '../miniapps/MiniAppModal';

export interface WidgetConfig {
  mode?: 'floating' | 'inline' | 'fullpage';
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  offset?: { x: number; y: number };
  lang?: string;
  customIcon?: string;
  welcomeMessage?: string;
  showWelcomeMessage?: boolean;
  showNotificationBubble?: boolean;
  enableAnimation?: boolean;
}

interface SharedWidgetProps {
  config: WidgetConfig;
  publicId?: string;
  preview?: boolean;
  isMobile?: boolean;
}

export function SharedWidget({ 
  config, 
  preview = false,
  isMobile = false,
  publicId
}: SharedWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [width, setWidth] = useState(isMobile ? window.innerWidth - 32 : 800);
  const [isResizing, setIsResizing] = useState(false);
  const { colors } = useDesignColors();
  const chatRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const defaultConfig: WidgetConfig = {
    mode: 'floating',
    position: 'bottom-right',
    offset: { x: 20, y: 20 },
    lang: 'fr',
    welcomeMessage: 'Bonjour ! Comment puis-je vous aider ?',
    showWelcomeMessage: true,
    showNotificationBubble: true,
    enableAnimation: true
  };

  // Merge with default config
  const mergedConfig = { ...defaultConfig, ...config };

  useEffect(() => {
    const timer = setTimeout(() => {
      setMounted(true);

      // Si le message de bienvenue est activé, on l'affiche après un délai
      if (mergedConfig.showWelcomeMessage) {
        setTimeout(() => {
          if (!isOpen) {
            setShowWelcome(true);
          }
        }, 3000);
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [mergedConfig.showWelcomeMessage, isOpen]);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (isMobile) {
        setWidth(window.innerWidth - 32);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Simplified useEffect - we no longer need to send button dimensions
  useEffect(() => {
    // Only send messages if this is embedded in an iframe (not in preview mode)
    if (!preview && typeof window !== "undefined" && window.parent && window.parent !== window) {
      // Send message to parent window
      window.parent.postMessage({
        type: 'botmaker-widget-state',
        isOpen: isOpen
      }, '*'); // In production, replace '*' with your domain for security
    }
  }, [isOpen, preview]);

  useEffect(() => {
    if (isOpen) {
      setShowWelcome(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !chatRef.current) return;
      const chatRect = chatRef.current.getBoundingClientRect();
      const newWidth = Math.max(640, Math.min(1200, chatRect.width + (chatRect.x - e.clientX)));
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Open automatically for inline and fullpage modes
  useEffect(() => {
    if (mergedConfig.mode !== 'floating') {
      setIsOpen(true);
    }
  }, [mergedConfig.mode]);

  // Determine widget position
  const positionStyle: React.CSSProperties = {};
  
  if (mergedConfig.position?.includes('bottom')) {
    positionStyle.bottom = mergedConfig.offset?.y || 20;
  } else {
    positionStyle.top = mergedConfig.offset?.y || 20;
  }
  
  if (mergedConfig.position?.includes('right')) {
    positionStyle.right = mergedConfig.offset?.x || 20;
  } else {
    positionStyle.left = mergedConfig.offset?.x || 20;
  }

  // For mobile devices, override position to bottom-right
  const finalPosition = isMobile 
    ? { bottom: 16, right: 16, top: 'auto', left: 'auto' }
    : positionStyle;

  // Pour mobile, assurer que le chat prend tout l'écran quand ouvert
  const mobileStyles = isMobile ? {
    position: 'fixed' as 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    zIndex: 999999,
    transform: 'none',
    borderRadius: 0
  } : {};

  // S'assurer que le message de bienvenue s'affiche si activé
  useEffect(() => {
    // Si le message de bienvenue est activé, l'afficher après un délai
    if (mergedConfig.showWelcomeMessage === true && mergedConfig.welcomeMessage && mounted && !isOpen) {
      const timer = setTimeout(() => {
        setShowWelcome(true);
      }, 3000);
      return () => clearTimeout(timer);
    } else {
      setShowWelcome(false);
    }
  }, [mergedConfig.showWelcomeMessage, mergedConfig.welcomeMessage, mounted, isOpen]);

  if (mergedConfig.mode === 'inline' || mergedConfig.mode === 'fullpage') {
    return (
      <div className="w-full h-full bg-transparent">
        <div className="w-full h-full rounded-xl overflow-hidden">
          <ChatInterface preview={preview} isWidget={true} publicId={publicId} />
        </div>
        <MiniAppModal />
      </div>
    );
  }

  return (
    <div className="relative" style={{ 
      position: 'fixed', 
      ...finalPosition, 
      zIndex: 999999,
      width: isMobile ? 'calc(100vw - 32px)' : `${width}px`,
      height: isMobile ? 'calc(100vh - 96px)' : '650px',
      background: 'transparent',
      border: 'none',
      padding: 0,
      margin: 0,
      overflow: 'hidden'
    }}>
      {/* Chat Interface */}
      <div 
        ref={chatRef}
        style={{ 
          width: isMobile ? 'calc(100vw - 32px)' : `${width}px`,
          position: 'fixed',
          ...(!isMobile ? finalPosition : {}),
          ...(finalPosition.bottom && !isMobile ? { bottom: (finalPosition.bottom as number) + 76 } : {}),
          height: isMobile ? 'calc(100vh - 96px)' : '650px',
          transformOrigin: mergedConfig.position?.includes('right') ? 'bottom right' : 'bottom left',
          maxWidth: isMobile ? '100vw' : '800px',
          maxHeight: isMobile ? 'calc(100vh - 80px)' : '650px',
          top: isMobile ? '0' : 'auto',
          padding: 0,
          margin: 0,
          backgroundColor: 'transparent',
          border: 'none',
          overflow: 'hidden',
          boxShadow: 'none',
          borderRadius: 0,
          outline: 'none',
          ...mobileStyles
        }}
        className={`
          transition-all duration-500
          ${isOpen 
            ? 'scale-100 opacity-100 translate-y-0' 
            : 'scale-0 opacity-0 translate-y-12 pointer-events-none'
          }
        `}
      >
        {/* Resize Handle - Only visible in preview mode */}
        {preview && !isMobile && (
          <div
            className={`
              absolute left-0 top-0 bottom-0 w-6 cursor-ew-resize
              flex items-center justify-center
              hover:bg-indigo-500/10 group
              ${isResizing ? 'bg-indigo-500/20' : ''}
            `}
            onMouseDown={() => setIsResizing(true)}
          >
            <div className={`
              w-0.5 h-12 rounded-full bg-indigo-500/40
              group-hover:bg-indigo-500/60
              ${isResizing ? 'bg-indigo-500' : ''}
            `} />
          </div>
        )}

        <div className="w-full h-full overflow-hidden rounded-xl">
          <ChatInterface preview={preview} isWidget={true} publicId={publicId} style={{
            border: 'none',
            outline: 'none',
            background: 'transparent'
          }} />
        </div>
      </div>

      {/* Welcome Message Bubble - Only shown if enabled */}
      {showWelcome && mergedConfig.showWelcomeMessage && mergedConfig.welcomeMessage && (
        <div 
          className={`
            absolute
            w-[320px] shadow-2xl
            ${mergedConfig.enableAnimation ? 'animate-fade-in' : ''}
          `}
          style={{
            bottom: (finalPosition.bottom as number) + 80,
            right: mergedConfig.position?.includes('right') ? (finalPosition.right as number) : 'auto',
            left: mergedConfig.position?.includes('left') ? (finalPosition.left as number) : 'auto',
            zIndex: 999997
          }}
        >
          <div 
            className="p-5 rounded-[24px] backdrop-blur-xl"
            style={{
              background: `linear-gradient(135deg, ${colors.gradientStart}99, ${colors.gradientEnd}99)`,
              boxShadow: `0 8px 32px -4px ${colors.gradientStart}40`
            }}
          >
            <div className="text-white text-lg font-medium leading-relaxed">
              {mergedConfig.welcomeMessage}
            </div>
          </div>
        </div>
      )}

      {/* Widget Button with Custom Icon */}
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={() => {
            setIsOpen(!isOpen);
          }}
          className={`
            relative rounded-full shadow-lg 
            flex items-center justify-center 
            transition-all duration-500
            hover:scale-110
            ${mounted ? 'animate-scale-in' : 'opacity-0 scale-50'}
            ${mergedConfig.customIcon && !isOpen ? 'overflow-hidden p-0' : 'p-4'}
          `}
          style={{
            background: mergedConfig.customIcon && !isOpen 
              ? 'transparent' 
              : `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
            width: isMobile ? '48px' : '64px',
            height: isMobile ? '48px' : '64px',
            position: 'fixed',
            ...finalPosition,
            zIndex: 999980
          }}
        >
          {isOpen ? (
            <div 
              className="w-full h-full rounded-full flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`
              }}
            >
              <X className={`text-white ${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`} />
            </div>
          ) : mergedConfig.customIcon ? (
            <img 
              src={mergedConfig.customIcon} 
              alt="Widget icon"
              className="w-full h-full object-cover rounded-full"
            />
          ) : (
            <MessageSquare className={`text-white ${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`} />
          )}

          {/* Notification Bubble - Only shown if enabled */}
          {!isOpen && mergedConfig.showNotificationBubble === true && (
            <span 
              className="absolute w-4 h-4 bg-red-500 rounded-full animate-pulse"
              style={{
                top: '-8px',
                right: '-4px',
                zIndex: 999985
              }}
            />
          )}
        </button>
      </div>
      
      {/* Inclure systématiquement le MiniAppModal pour permettre l'ouverture des mini-apps */}
      <MiniAppModal />
    </div>
  );
} 